// src/components/Website/WebsiteSummary.js
import React from 'react';
import { Card, Row, Col, ProgressBar } from 'react-bootstrap';

const WebsiteSummary = ({ websites }) => {
    // Pokud nemáme weby, vracíme null
    if (!websites || websites.length === 0) {
        return null;
    }

    // Počítání statistik
    const totalWebsites = websites.length;
    const onlineWebsites = websites.filter(w => w.status === 'online').length;
    const issuesWebsites = websites.filter(w => w.status === 'issues').length;
    const offlineWebsites = websites.filter(w => w.status === 'offline').length;

    // Procenta stavů
    const onlinePercent = Math.round((onlineWebsites / totalWebsites) * 100) || 0;
    const issuesPercent = Math.round((issuesWebsites / totalWebsites) * 100) || 0;
    const offlinePercent = Math.round((offlineWebsites / totalWebsites) * 100) || 0;

    // Průměrný uptime a security score z webů, kde máme data
    const uptimeData = websites
        .filter(w => typeof w.uptimePercentage === 'number')
        .map(w => w.uptimePercentage);

    const securityData = websites
        .filter(w => typeof w.securityScore === 'number')
        .map(w => w.securityScore);

    const avgUptime = uptimeData.length > 0
        ? Math.round(uptimeData.reduce((sum, val) => sum + val, 0) / uptimeData.length)
        : 'N/A';

    const avgSecurity = securityData.length > 0
        ? Math.round(securityData.reduce((sum, val) => sum + val, 0) / securityData.length)
        : 'N/A';

    // Nejhorší a nejlepší web podle security score (pokud máme data)
    let worstSecurity = null;
    let bestSecurity = null;

    const websitesWithSecurity = websites.filter(w => typeof w.securityScore === 'number');
    if (websitesWithSecurity.length > 0) {
        worstSecurity = websitesWithSecurity.reduce((prev, curr) =>
            prev.securityScore < curr.securityScore ? prev : curr);

        bestSecurity = websitesWithSecurity.reduce((prev, curr) =>
            prev.securityScore > curr.securityScore ? prev : curr);
    }

    return (
        <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-white">
                <h5 className="mb-0">Souhrnný přehled</h5>
            </Card.Header>
            <Card.Body>
                <Row className="mb-4">
                    <Col md={4} className="mb-3 mb-md-0">
                        <div className="d-flex align-items-center">
                            <div className="me-3">
                                <div className="avatar-circle bg-primary" style={{ width: '50px', height: '50px' }}>
                                    <i className="fas fa-globe text-white"></i>
                                </div>
                            </div>
                            <div>
                                <div className="text-muted">Weby celkem</div>
                                <div className="h3 mb-0">{totalWebsites}</div>
                            </div>
                        </div>
                    </Col>
                    <Col md={4} className="mb-3 mb-md-0">
                        <div className="d-flex align-items-center">
                            <div className="me-3">
                                <div className="avatar-circle bg-success" style={{ width: '50px', height: '50px' }}>
                                    <i className="fas fa-chart-line text-white"></i>
                                </div>
                            </div>
                            <div>
                                <div className="text-muted">Průměrný uptime</div>
                                <div className="h3 mb-0">{typeof avgUptime === 'number' ? `${avgUptime}%` : avgUptime}</div>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="d-flex align-items-center">
                            <div className="me-3">
                                <div className="avatar-circle bg-warning" style={{ width: '50px', height: '50px' }}>
                                    <i className="fas fa-shield-alt text-white"></i>
                                </div>
                            </div>
                            <div>
                                <div className="text-muted">Průměrná bezpečnost</div>
                                <div className="h3 mb-0">{typeof avgSecurity === 'number' ? `${avgSecurity}/100` : avgSecurity}</div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <div>Stav webů</div>
                        <div className="text-muted small">
                            Online: {onlineWebsites} • Problémy: {issuesWebsites} • Offline: {offlineWebsites}
                        </div>
                    </div>
                    <ProgressBar>
                        <ProgressBar variant="success" now={onlinePercent} key={1} />
                        <ProgressBar variant="warning" now={issuesPercent} key={2} />
                        <ProgressBar variant="danger" now={offlinePercent} key={3} />
                    </ProgressBar>
                </div>

                {worstSecurity && bestSecurity && (
                    <Row className="mt-4">
                        <Col md={6} className="mb-3 mb-md-0">
                            <div className="border-start border-success ps-3">
                                <div className="text-muted mb-1">Nejbezpečnější web</div>
                                <div className="h5 mb-1">{bestSecurity.name || bestSecurity.url}</div>
                                <div className="text-success">Skóre: {bestSecurity.securityScore}/100</div>
                            </div>
                        </Col>
                        {worstSecurity.securityScore < 80 && (
                            <Col md={6}>
                                <div className="border-start border-danger ps-3">
                                    <div className="text-muted mb-1">Web vyžadující pozornost</div>
                                    <div className="h5 mb-1">{worstSecurity.name || worstSecurity.url}</div>
                                    <div className="text-danger">Skóre: {worstSecurity.securityScore}/100</div>
                                </div>
                            </Col>
                        )}
                    </Row>
                )}
            </Card.Body>
        </Card>
    );
};

export default WebsiteSummary;